// CombatAnimationManager - 戰鬥動畫管理器
import Phaser from 'phaser';
import type { CombatResult } from '../../types';

/**
 * 動畫設定
 */
export interface AnimationConfig {
  attackDuration: number;
  attackEase: string;
  shakeDuration: number;
  shakeIntensity: number;
  destroyDuration: number;
  upgradeDuration: number;
}

const DEFAULT_CONFIG: AnimationConfig = {
  attackDuration: 300,
  attackEase: 'Power2',
  shakeDuration: 100,
  shakeIntensity: 5,
  destroyDuration: 400,
  upgradeDuration: 500,
};

/**
 * 戰鬥動畫管理器
 */
export class CombatAnimationManager {
  private scene: Phaser.Scene;
  private config: AnimationConfig;

  constructor(scene: Phaser.Scene, config?: Partial<AnimationConfig>) {
    this.scene = scene;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * 播放攻擊動畫 — lunge + hit flash
   */
  async playAttackAnimation(
    attackerSprite: Phaser.GameObjects.Container,
    targetPosition: { x: number; y: number },
    onHit?: () => void
  ): Promise<void> {
    const originalX = attackerSprite.x;
    const originalY = attackerSprite.y;

    const deltaX = (targetPosition.x - originalX) * 0.5;
    const deltaY = (targetPosition.y - originalY) * 0.5;

    return new Promise((resolve) => {
      // Lunge toward target
      this.scene.tweens.add({
        targets: attackerSprite,
        x: originalX + deltaX,
        y: originalY + deltaY,
        duration: this.config.attackDuration / 2,
        ease: this.config.attackEase,
        onComplete: () => {
          // Hit flash
          this.scene.cameras.main.flash(80, 255, 255, 255);
          onHit?.();

          // Return to original position
          this.scene.tweens.add({
            targets: attackerSprite,
            x: originalX,
            y: originalY,
            duration: this.config.attackDuration / 2,
            ease: this.config.attackEase,
            onComplete: () => { resolve(); },
          });
        },
      });
    });
  }

  /**
   * 播放受擊震動
   */
  async playShakeAnimation(sprite: Phaser.GameObjects.Container): Promise<void> {
    const originalX = sprite.x;
    const intensity = this.config.shakeIntensity;

    return new Promise((resolve) => {
      // 快速左右震動
      this.scene.tweens.add({
        targets: sprite,
        x: originalX + intensity,
        duration: this.config.shakeDuration / 4,
        yoyo: true,
        repeat: 3,
        ease: 'Sine.easeInOut',
        onComplete: () => {
          sprite.x = originalX;
          resolve();
        },
      });
    });
  }

  /**
   * 播放傷害數字
   */
  showDamageNumber(x: number, y: number, damage: number, isWinner: boolean): void {
    const color = isWinner ? '#00ff00' : '#ff0000';
    const text = this.scene.add.text(x, y - 20, String(damage), {
      fontSize: '32px',
      color,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
    });
    text.setOrigin(0.5, 0.5);
    text.setDepth(1000);

    // 向上漂浮並淡出
    this.scene.tweens.add({
      targets: text,
      y: y - 80,
      alpha: 0,
      duration: 800,
      ease: 'Power2',
      onComplete: () => { text.destroy(); },
    });
  }

  /**
   * 播放破壞動畫 — with particle shatter effect
   */
  async playDestroyAnimation(sprite: Phaser.GameObjects.Container): Promise<void> {
    // Spawn shatter particles
    this.spawnDestroyParticles(sprite.x, sprite.y);

    // Brief screen shake
    this.scene.cameras.main.shake(150, 0.005);

    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: sprite,
        scaleX: 0,
        scaleY: 0,
        angle: 360,
        alpha: 0,
        duration: this.config.destroyDuration,
        ease: 'Back.easeIn',
        onComplete: () => { resolve(); },
      });
    });
  }

  /**
   * Spawn particle shatter effect at position
   */
  private spawnDestroyParticles(x: number, y: number): void {
    const particleCount = 10;
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2 + Math.random() * 0.5;
      const speed = 40 + Math.random() * 60;
      const targetX = x + Math.cos(angle) * speed;
      const targetY = y + Math.sin(angle) * speed;
      const size = 2 + Math.random() * 4;
      const color = Math.random() > 0.5 ? 0xff4444 : 0xffaa44;

      const p = this.scene.add.rectangle(x, y, size, size, color, 0.9);
      p.setDepth(1500);

      this.scene.tweens.add({
        targets: p,
        x: targetX,
        y: targetY + 30,
        alpha: 0,
        scale: { from: 1, to: 0.1 },
        duration: 400 + Math.random() * 200,
        ease: 'Power2',
        onComplete: () => { p.destroy(); },
      });
    }
  }

  /**
   * 播放升級動畫
   */
  async playUpgradeAnimation(sprite: Phaser.GameObjects.Container): Promise<void> {
    const originalScaleX = sprite.scaleX;
    const originalScaleY = sprite.scaleY;

    return new Promise((resolve) => {
      // 發光效果
      const glow = this.scene.add.rectangle(sprite.x, sprite.y, 120, 160, 0xffff00, 0.5);
      glow.setDepth(sprite.depth - 1);

      // 閃爍
      this.scene.tweens.add({
        targets: glow,
        alpha: 0,
        duration: this.config.upgradeDuration / 2,
        yoyo: true,
        repeat: 1,
        onComplete: () => { glow.destroy(); },
      });

      // 放大再縮回
      this.scene.tweens.add({
        targets: sprite,
        scaleX: originalScaleX * 1.3,
        scaleY: originalScaleY * 1.3,
        duration: this.config.upgradeDuration / 2,
        yoyo: true,
        ease: 'Bounce.easeOut',
        onComplete: () => { resolve(); },
      });
    });
  }

  /**
   * 播放效果觸發提示
   */
  showEffectTrigger(x: number, y: number, effectName: string): void {
    const text = this.scene.add.text(x, y, effectName, {
      fontSize: '18px',
      color: '#ffff00',
      backgroundColor: '#000000aa',
      padding: { x: 8, y: 4 },
    });
    text.setOrigin(0.5, 0.5);
    text.setDepth(1000);

    // 向上漂浮並淡出
    this.scene.tweens.add({
      targets: text,
      y: y - 40,
      alpha: 0,
      duration: 1200,
      ease: 'Power2',
      onComplete: () => { text.destroy(); },
    });
  }

  /**
   * 播放完整戰鬥序列
   */
  async playCombatSequence(
    attackerSprite: Phaser.GameObjects.Container,
    defenderSprite: Phaser.GameObjects.Container,
    result: CombatResult,
    onDefenderDestroyed?: () => void,
    onAttackerDestroyed?: () => void
  ): Promise<void> {
    // 1. 攻擊動畫
    await this.playAttackAnimation(
      attackerSprite,
      { x: defenderSprite.x, y: defenderSprite.y },
      () => {
        // 命中時震動防守者（fire and forget）
        void this.playShakeAnimation(defenderSprite);
      }
    );

    // 2. 顯示傷害數字
    this.showDamageNumber(
      attackerSprite.x,
      attackerSprite.y,
      result.attackerAtk,
      result.winner === 'attacker'
    );
    this.showDamageNumber(
      defenderSprite.x,
      defenderSprite.y,
      result.defenderAtk,
      result.winner === 'defender'
    );

    // 3. 等待一下
    await this.delay(200);

    // 4. 破壞動畫 + winner bounce
    if (result.winner === 'attacker') {
      await this.playDestroyAnimation(defenderSprite);
      onDefenderDestroyed?.();
      // Winner bounce
      this.playWinnerBounce(attackerSprite);
    } else {
      // winner === 'defender'
      await this.playDestroyAnimation(attackerSprite);
      onAttackerDestroyed?.();
      this.playWinnerBounce(defenderSprite);
    }
  }

  /**
   * Winner bounce — slight celebratory bounce after winning combat
   */
  private playWinnerBounce(sprite: Phaser.GameObjects.Container): void {
    const origScaleX = sprite.scaleX;
    const origScaleY = sprite.scaleY;
    this.scene.tweens.add({
      targets: sprite,
      scaleX: origScaleX * 1.15,
      scaleY: origScaleY * 1.15,
      duration: 150,
      yoyo: true,
      ease: 'Bounce.easeOut',
    });
  }

  /**
   * 延遲
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.scene.time.delayedCall(ms, () => { resolve(); });
    });
  }
}
